import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../services/LanguageContextProvider'
import { LOCALES, localeToHreflang, localizedUrl, SITE_URL } from '../i18n/locales'

// Per-locale, per-route <head>: title, description, canonical, Open Graph,
// Twitter, <html lang> and the full hreflang cluster (incl. x-default -> FI).
// Titles/descriptions come from the `seo.<pageKey>` i18n block.
export const Seo = ({ pageKey, sub = '', image = `${SITE_URL}/logo.webp` }) => {
  const { t } = useTranslation('global')
  const { language } = useLanguage()

  const lang = localeToHreflang[language] ?? 'fi'
  const canonical = localizedUrl(language, sub)
  const homeUrl = localizedUrl(language, '')
  const title = t(`seo.${pageKey}.title`)
  const description = t(`seo.${pageKey}.description`)

  // Per-route structured data. Home gets WebSite (linked to the site-wide
  // Organization in index.html via @id); sub-pages get a BreadcrumbList.
  // /horeca already ships FAQPage from HorecaFaq — don't duplicate it here.
  let jsonLd = null
  if (pageKey === 'home') {
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: homeUrl,
      name: 'Åby',
      inLanguage: lang,
      publisher: { '@id': `${SITE_URL}/#organization` },
    }
  } else {
    const crumb = pageKey === 'cookies' ? t('cookies.policy.title') : t('header.navbar.linkHoreca')
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Åby', item: homeUrl },
        { '@type': 'ListItem', position: 2, name: crumb, item: canonical },
      ],
    }
  }

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Åby" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {LOCALES.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={localeToHreflang[loc]}
          href={localizedUrl(loc, sub)}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={localizedUrl('FI', sub)} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}
